/**
 * recalc-ui.ts — the version banner and the recalculation diff (P2/US-215, D10).
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * A BANNER ON EVERY CASE IS A BANNER NOBODY READS
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * {@link versionBannerHTML} returns the **empty string** when the stored versions match the running
 * ones, which will be almost every case almost all of the time. That is the whole design: the
 * banner has to mean something the day it appears, and a persistent strip saying "calcul du
 * 05/09/2026" trains people to look past exactly the place a real warning will be.
 *
 * When it does appear it **names the version that produced the result**, not merely that something
 * differs. "Moteur 1.0.0, catalogue 2026.08" is a fact an engineer can act on — check the changelog,
 * ask whether this affects them — while "ce cas est peut-etre obsolete" is an anxiety.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * "RIEN N'A CHANGE" IS THE MOST COMMON ANSWER AND MUST BE THE CLEAREST ONE
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * While the engine is frozen, every preview returns an identical diff. An engineer asking "is my
 * two-year-old note still current?" deserves a sentence saying so, not an empty table they have to
 * interpret as good news. `identical` is rendered as its own statement, above everything else.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * THE CONFIRMATION SAYS WHAT IT DOES, INCLUDING THE PART THAT REASSURES
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * "Confirmer" alone is a dialog people dismiss. The sentence says the previous result is **kept as
 * a revision, not overwritten** — which is true (US-208 archives it with its own integrity hash) and
 * is exactly the fact that makes the decision safe to take. A note may already have been issued from
 * the old result; the user needs to know that record survives.
 */

import type { CheckDiff, RecalcDiff, VersionStamp } from '@ronceray/contract';

import { escapeHtml } from './dom';
import { ratio3 } from './format';

/** Do two stamps agree? Both fields, because a catalog change moves numbers exactly as code does. */
export function sameVersions(a: VersionStamp, b: VersionStamp): boolean {
  return a.engineVersion === b.engineVersion && a.catalogVersion === b.catalogVersion;
}

/**
 * The banner, or nothing at all.
 *
 * Deliberately returns `''` rather than a hidden element: an empty string cannot be revealed by a
 * stray CSS rule, and there is then no element for a future change to accidentally always show.
 */
export function versionBannerHTML(stored: VersionStamp, running: VersionStamp): string {
  if (sameVersions(stored, running)) return '';

  /*
   * No button here. `Recalculer` lives in the open-case strip and is available on EVERY stored
   * case, not only on one whose versions have moved: "is this still current?" is a question worth
   * answering when the answer is yes, and US-215 asks for the diff view to be legible when nothing
   * changed. This banner's job is to say what produced the number on screen.
   */
  return (
    '<div class="p2-banner" id="p2-version-banner">' +
    `<span>Ce resultat a ete calcule avec le moteur ${escapeHtml(stored.engineVersion)} ` +
    `et le catalogue ${escapeHtml(stored.catalogVersion)}. ` +
    `La version actuelle est ${escapeHtml(running.engineVersion)} / ` +
    `${escapeHtml(running.catalogVersion)}.</span>` +
    '</div>'
  );
}

/** The three kinds, in French. One word each, because they head a table column. */
export const DIFF_KIND_LABELS: Record<CheckDiff['kind'], string> = {
  changed: 'modifie',
  appeared: 'apparu',
  disappeared: 'disparu',
};

export const NOTHING_CHANGED_MESSAGE =
  "Rien n'a change : le calcul actuel donne exactement le meme resultat que celui enregistre.";

/**
 * A ratio for the diff table.
 *
 * `null` and non-finite both render as `-`, matching how the checks table renders a check that does
 * not apply. The contract is explicit that the two are the same thing — no usable magnitude — and
 * showing `0.000` for either would read as a passing check.
 */
export function diffRatio(value: number | null): string {
  return value === null || !Number.isFinite(value) ? '-' : ratio3(value);
}

/** The diff table. Only the checks that moved; the rest are counted in the sentence below it. */
export function diffTableHTML(diff: RecalcDiff): string {
  if (diff.checks.length === 0) return '';

  let h =
    '<table class="checks"><thead><tr><th>Verification</th><th>Avant</th><th>Apres</th>' +
    '<th>Statut</th></tr></thead><tbody>';

  for (const check of diff.checks) {
    h +=
      `<tr><td>${escapeHtml(check.label)}</td>` +
      `<td class="num">${diffRatio(check.beforeRatio)}</td>` +
      `<td class="num">${diffRatio(check.afterRatio)}</td>` +
      `<td>${DIFF_KIND_LABELS[check.kind]}</td></tr>`;
  }

  return `${h}</tbody></table>`;
}

/** The whole diff view: the verdict, the table, and the two buttons. */
export function diffHTML(diff: RecalcDiff): string {
  const verdict = diff.identical
    ? `<p class="vide">${NOTHING_CHANGED_MESSAGE}</p>`
    : `<p class="hint">Taux ELU determinant : ${ratio3(diff.maxBefore)} avant, ` +
      `${ratio3(diff.maxAfter)} apres. ${diff.unchangedCount} verification(s) inchangee(s).</p>`;

  return `<section class="card p2-diff"><h2>Recalcul</h2>
<p class="hint">Enregistre avec le moteur ${escapeHtml(diff.from.engineVersion)} / catalogue
${escapeHtml(diff.from.catalogVersion)}. Version actuelle : ${escapeHtml(diff.to.engineVersion)} /
${escapeHtml(diff.to.catalogVersion)}.</p>
${verdict}
${diffTableHTML(diff)}
<p class="hint">${CONFIRM_EXPLANATION}</p>
<div class="tools"><button type="button" id="p2-diff-confirm">Confirmer le recalcul</button>
<button type="button" class="mini" id="p2-diff-cancel">Annuler</button></div>
</section>`;
}

/** Said above the confirm button, every time. See this file's header. */
export const CONFIRM_EXPLANATION =
  'En confirmant, le resultat enregistre est remplace par le calcul actuel. Le resultat precedent ' +
  "est conserve comme revision — il n'est pas efface, car une note de calcul a pu etre emise a " +
  'partir de lui.';

/* ================================================================================================
 * The controller
 * ============================================================================================== */

export interface RecalcDeps {
  readonly preview: (caseId: string) => Promise<{ ok: boolean; value?: { diff: RecalcDiff } }>;
  readonly confirm: (caseId: string) => Promise<{ ok: boolean }>;
  /** Called after a confirmed recalculation, so the case view can reload its snapshot. */
  readonly onConfirmed: (caseId: string) => void | Promise<void>;
  readonly onCancel: () => void;
}

/**
 * Preview, show, confirm.
 *
 * **Nothing is written until `confirm`**, and the preview is fetched fresh each time the user asks
 * rather than cached from an earlier visit: a diff shown against a version that has since moved
 * would be a decision taken on stale evidence, which is the failure this whole flow exists to
 * prevent.
 */
export function recalcView(host: HTMLElement, deps: RecalcDeps) {
  return {
    async show(caseId: string): Promise<void> {
      const previewed = await deps.preview(caseId);
      if (!previewed.ok || previewed.value === undefined) {
        host.innerHTML =
          '<section class="card p2-diff"><p class="vide">Le recalcul n a pas pu etre effectue.' +
          '</p></section>';
        return;
      }

      host.innerHTML = diffHTML(previewed.value.diff);
      host.hidden = false;

      host.querySelector<HTMLButtonElement>('#p2-diff-confirm')?.addEventListener('click', () => {
        void deps.confirm(caseId).then(async (result) => {
          if (!result.ok) return;
          host.innerHTML = '';
          host.hidden = true;
          await deps.onConfirmed(caseId);
        });
      });

      host.querySelector<HTMLButtonElement>('#p2-diff-cancel')?.addEventListener('click', () => {
        host.innerHTML = '';
        host.hidden = true;
        deps.onCancel();
      });
    },

    hide(): void {
      host.innerHTML = '';
      host.hidden = true;
    },
  };
}
